import React, { useEffect, useMemo, useState } from 'react';
import ListPageLayout from '../Components/ui/ListPageLayout';
import DataTable from '../Components/ui/DataTable';
import AppModal from '../Components/ui/AppModal';
import { getDecryptedData } from '../utils/authStorage';
import { getTlClients, getClientById } from '../services/clientService';
import { getApiErrorMessage } from '../utils/apiError';

const normalizeAssignedClients = (tlClients) => {
  const clients = tlClients?.Client || tlClients?.client || tlClients?.clients || [];
  return Array.isArray(clients) ? clients : [];
};

function TeamLeadClients() {
  const userId = getDecryptedData('Id');
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    if (!userId) return;
    const load = async () => {
      setLoading(true);
      setLoadError('');
      try {
        const tlClients = await getTlClients(userId);
        setClients(normalizeAssignedClients(tlClients));
      } catch (error) {
        setLoadError(getApiErrorMessage(error, 'Failed to load assigned clients.'));
        setClients([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userId]);

  const openClientDetail = async (clientId) => {
    try {
      const detail = await getClientById(clientId);
      setSelectedClient(detail);
      setShowDetail(true);
    } catch (error) {
      setLoadError(getApiErrorMessage(error, 'Failed to load client details.'));
    }
  };

  const columns = useMemo(() => [
    {
      key: 'ClientCode',
      label: 'Client Code',
      sortable: true,
      render: (row) => (
        <button type="button" className="btn btn-link p-0" onClick={() => openClientDetail(row.id)}>
          <b>{row.ClientCode}</b>
        </button>
      ),
    },
    { key: 'ClientName', label: 'Client Name', sortable: true },
    { key: 'Services', label: 'Services', sortable: true },
    { key: 'Location', label: 'Location', sortable: true },
  ], []);

  const tableData = clients.map((client) => ({
    id: client._id,
    ClientCode: client.ClientCode || '—',
    ClientName: client.ClientName || '—',
    Services: client.Services || '—',
    Location: client.Location || '—',
  }));

  return (
    <ListPageLayout
      title="Assigned Clients"
      breadcrumbs={[
        { label: 'Dashboard', path: '/dashboard' },
        { label: 'Assigned Clients' },
      ]}
      subtitle="Client accounts assigned to you for recruitment work."
      backTo="/dashboard"
      error={loadError}
    >
      {loading && <p className="hr-empty-state">Loading clients...</p>}
      {!loading && !loadError && clients.length === 0 && (
        <p className="hr-empty-state">
          No clients assigned yet. Ask an admin to assign clients to your account.
        </p>
      )}
      {!loading && clients.length > 0 && (
        <DataTable
          columns={columns}
          data={tableData}
          searchPlaceholder="Search by code, name, or location"
          exportFileName="assigned-clients"
        />
      )}

      <AppModal
        show={showDetail}
        onHide={() => setShowDetail(false)}
        title={selectedClient?.ClientName || 'Client Details'}
        size="lg"
      >
        {selectedClient && (
          <div className="hr-detail-grid">
            <p><strong>Client Code:</strong> {selectedClient.ClientCode || '—'}</p>
            <p><strong>Services:</strong> {selectedClient.Services || '—'}</p>
            <p><strong>Location:</strong> {selectedClient.Location || '—'}</p>
            <p><strong>Contact:</strong> {selectedClient.Name || '—'} ({selectedClient.Spoc || '—'})</p>
            <p><strong>Mobile:</strong> {selectedClient.MobileNumber || '—'}</p>
            <p><strong>Email:</strong> {selectedClient.Email || '—'}</p>
          </div>
        )}
      </AppModal>
    </ListPageLayout>
  );
}

export default TeamLeadClients;
